import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

/** @type {ResizeObserver | null} */
let headerGroupResizeObserver = null;
/** @type {MutationObserver | null} */
let headerGroupMutationObserver = null;
/** @type {() => void | null} */
let headerGroupPageHideCleanup = null;
/** @type {number | null} */
let headerGroupHeightRafId = null;
const PASSIVE_SCROLL_OPTIONS = { passive: true };

/**
 * Normalize ResizeObserverEntry block size across browser implementations.
 * @param {ResizeObserverEntry} entry
 * @returns {number}
 */
function getEntryBlockSize(entry) {
  const { borderBoxSize } = entry;
  if (Array.isArray(borderBoxSize)) {
    return borderBoxSize[0]?.blockSize ?? entry.contentRect.height;
  }
  return borderBoxSize?.blockSize ?? entry.contentRect.height;
}

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessary reflows.
    const roundedHeaderHeight = Math.round(getEntryBlockSize(entry));
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        window.addEventListener('scroll', this.#handleWindowScroll, PASSIVE_SCROLL_OPTIONS);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    window.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    const connectedHeaders = Array.from(document.querySelectorAll('header-component')).filter((headerComponent) =>
      headerComponent.isConnected
    );
    if (connectedHeaders.length === 0) {
      document.body.style.setProperty('--header-height', '0px');
    }
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    headerGroupResizeObserver?.disconnect();
    headerGroupMutationObserver?.disconnect();
    if (headerGroupPageHideCleanup) {
      window.removeEventListener('pagehide', headerGroupPageHideCleanup);
      headerGroupPageHideCleanup = null;
    }

    const syncHeaderGroupHeight = () => {
      if (!headerGroup.isConnected) return;
      const includeHeaderHeight =
        header instanceof HTMLElement &&
        header.hasAttribute('transparent') &&
        Boolean(header.parentElement?.nextElementSibling);

      let headerGroupHeight = 0;

      // Include detached header height only when needed by transparent overlays.
      if (includeHeaderHeight && header instanceof HTMLElement && !headerGroup.contains(header)) {
        headerGroupHeight += header.getBoundingClientRect().height;
      }

      const children = headerGroup.children;
      for (let i = 0; i < children.length; i++) {
        const element = children[i];
        if (!(element instanceof HTMLElement)) continue;
        if (element === header && !includeHeaderHeight) continue;
        headerGroupHeight += element.getBoundingClientRect().height;
      }

      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    };

    const scheduleHeaderGroupHeightSync = () => {
      if (headerGroupHeightRafId !== null) return;
      headerGroupHeightRafId = requestAnimationFrame(() => {
        headerGroupHeightRafId = null;
        syncHeaderGroupHeight();
      });
    };

    const observeHeaderGroupElements = () => {
      if (!headerGroupResizeObserver) return;
      headerGroupResizeObserver.disconnect();

      if (header instanceof HTMLElement) {
        headerGroupResizeObserver.observe(header);
      }
      headerGroupResizeObserver.observe(headerGroup);

      const children = headerGroup.children;
      for (let i = 0; i < children.length; i++) {
        const element = children[i];
        if (element instanceof HTMLElement) {
          headerGroupResizeObserver.observe(element);
        }
      }
    };

    headerGroupResizeObserver = new ResizeObserver(() => {
      scheduleHeaderGroupHeightSync();
    });

    observeHeaderGroupElements();
    scheduleHeaderGroupHeightSync();

    // Also observe the header group itself for child changes
    headerGroupMutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          observeHeaderGroupElements();
          scheduleHeaderGroupHeightSync();
        }
      }
    });

    headerGroupMutationObserver.observe(headerGroup, { childList: true });

    headerGroupPageHideCleanup = () => {
      if (headerGroupHeightRafId !== null) {
        cancelAnimationFrame(headerGroupHeightRafId);
        headerGroupHeightRafId = null;
      }
      headerGroupResizeObserver?.disconnect();
      headerGroupResizeObserver = null;
      headerGroupMutationObserver?.disconnect();
      headerGroupMutationObserver = null;
    };
    window.addEventListener('pagehide', headerGroupPageHideCleanup);
  }
});
